import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PasswordReset.css';

const PasswordReset: React.FC = () => {
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();

  const checkPasswordRequirement = (requirement: string): boolean => {
    switch (requirement) {
      case 'length':
        return newPassword.length >= 8;
      case 'capital':
        return /[A-Z]/.test(newPassword);
      case 'lowercase':
        return /[a-z]/.test(newPassword);
      case 'number':
        return /[0-9]/.test(newPassword);
      case 'symbol':
        return /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
      default:
        return false;
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  const handleResend = () => {
    setIsResending(true);
    // Mock resend functionality
    setTimeout(() => {
      setIsResending(false);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }, 1000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      alert('Please enter a valid 6-digit OTP');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    // Check all password requirements
    const allRequirementsMet = ['length', 'capital', 'lowercase', 'number', 'symbol']
      .every(req => checkPasswordRequirement(req));
    
    if (!allRequirementsMet) {
      alert('Please meet all password requirements');
      return;
    }
    
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      // Navigate to success page or login page
    }, 3000);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="password-reset-container">
      <header className="password-reset-header">
        <button 
          className="back-button" 
          onClick={handleBack}
          aria-label="Go back"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path 
              d="M10 12L6 8L10 4" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </header>

      <div className="password-reset-content">
        <div className="password-reset-form-wrapper">
          <h1 className="password-reset-title">Reset Password</h1>
          <p className="password-reset-subtitle">
            Enter the 6 digit code sent to <span className="email-highlight">d***************e@gmail.com</span>
          </p>

          <form onSubmit={handleSubmit} className="password-reset-form">
            <div className="form-group">
              <input
                type="text"
                className="otp-input"
                placeholder="747838"
                value={otp}
                onChange={handleOtpChange}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
              />
            </div>

            <div className="otp-resend-section">
              <span className="resend-text">Didn't receive OTP?</span>
              <button
                type="button"
                className="resend-button"
                onClick={handleResend}
                disabled={isResending}
              >
                {isResending ? 'Resending...' : 'Resend'}
              </button>
            </div>

            <div className="form-group password-group">
              <label htmlFor="newPassword" className="form-label">
                New Password
              </label>
              <div className="password-input-wrapper">
                <input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    {showNewPassword ? (
                      <path d="M14 8C14 8 11.5 12 8 12C4.5 12 2 8 2 8C2 8 4.5 4 8 4C11.5 4 14 8 14 8Z M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <>
                        <path d="M14 8C14 8 11.5 12 8 12C4.5 12 2 8 2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 8C2 8 4.5 4 8 4C11.5 4 14 8 14 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 2L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div className="password-requirements">
              <div className="requirements-badges">
                <span className={`requirement-badge ${checkPasswordRequirement('length') ? 'met' : ''}`}>
                  Min. 8 characters
                </span>
                <span className={`requirement-badge ${checkPasswordRequirement('capital') ? 'met' : ''}`}>
                  Capital
                </span>
                <span className={`requirement-badge ${checkPasswordRequirement('lowercase') ? 'met' : ''}`}>
                  Lowercase
                </span>
                <span className={`requirement-badge ${checkPasswordRequirement('number') ? 'met' : ''}`}>
                  Number
                </span>
                <span className={`requirement-badge ${checkPasswordRequirement('symbol') ? 'met' : ''}`}>
                  Symbol
                </span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password
              </label>
              <div className="password-input-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    {showConfirmPassword ? (
                      <path d="M14 8C14 8 11.5 12 8 12C4.5 12 2 8 2 8C2 8 4.5 4 8 4C11.5 4 14 8 14 8Z M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <>
                        <path d="M14 8C14 8 11.5 12 8 12C4.5 12 2 8 2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 8C2 8 4.5 4 8 4C11.5 4 14 8 14 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 2L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <button type="submit" className="reset-password-button">
              Reset password
            </button>
          </form>
        </div>
      </div>

      {showSuccess && (
        <div className="success-notification">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path 
              d="M16.667 5L7.5 14.167L3.333 10" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          <span>Password reset successfully!</span>
        </div>
      )}
    </div>
  );
};

export default PasswordReset;